import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';
import { Box, useTheme } from '@mui/material';

import { Button, Skeleton, Typography } from '@zendesk/forethought-elements';
import MarkdownBoldText from './MarkdownBoldText';
import { useGetAbTestAIRecommendationQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import { SolveABTestStatus } from 'src/services/agent-ab-test/types';

interface RecommendationCardProps {
  status: SolveABTestStatus | undefined;
}

export default function RecommendationCard({
  status,
}: RecommendationCardProps) {
  const theme = useTheme();
  const { abTestId = '' } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation('agent-ab-test');

  const isRunning = status === 'running';
  const isCompleted = status === 'completed' || status === 'canceled';

  const {
    data: recommendation,
    isError,
    isFetching: isLoading,
  } = useGetAbTestAIRecommendationQuery(abTestId, {
    skip: !abTestId || (!isRunning && !isCompleted),
  });

  const handleViewConversations = () => {
    setSearchParams({ tab: 'conversations' });
  };

  const loadingSkeleton = (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Skeleton height={24} variant='rounded' width={180} />
      <Skeleton height={16} variant='rounded' />
      <Skeleton height={16} variant='rounded' width='90%' />
      <Skeleton height={16} variant='rounded' width='70%' />
      <Skeleton height={24} variant='rounded' width={140} />
      <Skeleton height={16} variant='rounded' />
      <Skeleton height={16} variant='rounded' width='80%' />
      <Skeleton height={16} variant='rounded' width='60%' />
    </Box>
  );

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.colors.slate[200]}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        maxHeight: '410px',
        minHeight: '370px',
        padding: '24px',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          loadingSkeleton
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Recommendation section */}
            <Typography variant='font18Bold'>
              {t('recommendationCard.heading')}
            </Typography>

            {isRunning ? (
              <Typography
                color={theme.palette.colors.grey[600]}
                variant='font14'
              >
                {t('recommendationCard.earlySignalsDisclaimer')}
              </Typography>
            ) : isCompleted && recommendation?.recommendation ? (
              <Typography variant='font14'>
                <MarkdownBoldText text={recommendation.recommendation} />
              </Typography>
            ) : isError ? (
              <Typography
                color={theme.palette.colors.grey[600]}
                variant='font14'
              >
                {t('recommendationCard.errorText')}
              </Typography>
            ) : null}

            {/* Key Learning section */}
            <Typography variant='font18Bold'>
              {t('recommendationCard.keyLearningHeading')}
            </Typography>

            {recommendation?.key_learnings &&
            recommendation.key_learnings.length > 0 ? (
              <Box
                component='ul'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  listStyleType: 'disc',
                  margin: 0,
                  paddingLeft: '20px',
                }}
              >
                {recommendation.key_learnings.map((learning, index) => (
                  <Box component='li' key={index}>
                    <Typography variant='font14'>
                      <MarkdownBoldText text={learning} />
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography
                color={theme.palette.colors.grey[600]}
                variant='font14'
              >
                {t('recommendationCard.noKeyLearnings')}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Bottom buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          mt: '16px',
        }}
      >
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.colors.grey[100]}`,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleViewConversations}
            size='medium'
            variant='secondary'
          >
            {t('recommendationCard.viewConversationsButton')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
